import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Info, type LucideIcon } from '../../../icons';
import './InlineNote.css';

/**
 * InlineNote — **이 폼 · 이 카드에 대한 지금의 판정** 한 줄 (디자인 정본 D13 · 2026-09-15 첫 코드).
 *
 * 예: 「게시판 알림 대상 밖 3명은 빠집니다」 · 「알림 사용이 꺼져 있어 잠겨 있습니다」.
 *
 * ★★ **값이 들어간 문장만** 담는다. 늘 같은 문장이면 이건 InlineNote 가 아니라 **도움말**이다(정본 P8).
 * ★ **닫기 없음** — 값이 바뀌면 문장도 바뀌므로 닫을 이유가 없다.
 * ★ **행동 단추 없음** — 정본 D13 규약이다. 「확인했어요」 같은 행동이 붙는 띠는 별도 제안(CONFIRMBAND)이다.
 * ★ Banner 와 다르다 — Banner 는 화면 전체의 상태(연결 끊김), InlineNote 는 이 자리의 것.
 * ★ 색은 상태색 tint/ink 짝(F4) — 네 톤 모두 4.5:1 을 넘는다.
 */
export type InlineNoteTone = 'info' | 'success' | 'warning' | 'danger';

const TONE_ICON: Record<InlineNoteTone, LucideIcon> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  danger: AlertCircle,
};

export interface InlineNoteProps {
  tone?: InlineNoteTone;
  /** 톤 기본 아이콘 대신 쓸 것. `null` 이면 아이콘을 그리지 않는다. */
  icon?: LucideIcon | null;
  children: ReactNode;
  className?: string;
}

export function InlineNote({ tone = 'info', icon, children, className }: InlineNoteProps) {
  const Icon = icon === null ? null : (icon ?? TONE_ICON[tone]);
  return (
    <div
      className={['wc-inline-note', `wc-inline-note--${tone}`, className ?? ''].filter(Boolean).join(' ')}
      role="note"
    >
      {Icon && <Icon className="wc-inline-note__icon" aria-hidden />}
      <div className="wc-inline-note__body">{children}</div>
    </div>
  );
}
